/* eslint-disable no-nested-ternary */
import React from 'react';
import Form from '../../../../../../../../shared/design-system/components/organisms/form';
import { RequestStatus } from '../../../../../../../../shared/enums/request-status';
import {
  FormField,
  FormMode,
  InputValidationType,
} from '../../../../../../../../shared/design-system/components/organisms/form/types';
import Spinner, {
  SpinnerAnimation,
} from '../../../../../../../../shared/design-system/components/atoms/spinner/spinner';
import { SelectedProspectData } from '../../../../../../../sequence/types';
import {
  executeOnRequestStatusWithPrevStatusCheck,
  getIsRequestPending,
} from '../../../../../../../../shared/utils';
import toaster from '../../../../../../../../shared/toaster';
import { Field } from '../../../../../../../settings/types/field';
import { FieldType } from '../../../../../../../settings/enums/field';
import { formatDateForApi } from '../helpers/prospect-fields';

type IProps = {
  selectedProspectData: SelectedProspectData;
  selectedProspectId: number;
  onClose: () => void;
  handleSaveContactAttributesSubmit: (attributes) => void;
  getSingleProspectDetailsStatus: RequestStatus;
  saveContactAttributesRequestStatus: RequestStatus;
  sendGetSingleProspectDetailsRequest: (contactId: number) => void;
  sendPrimaryEmailChangeInProspectRequest: (
    primaryEmailProspectInfoId: number,
    contactId: number,
  ) => void;
  sendPrimaryPhoneChangeInProspectRequest: (
    primaryPhoneProspectInfoId: number,
    contactId: number,
  ) => void;
  sendDeleteEmailFromProspectRequest: (
    prospectInfoId: number,
    contactId: number,
  ) => void;
  sendDeletePhoneFromProspectRequest: (
    prospectInfoId: number,
    contactId: number,
  ) => void;
  deleteSecondaryEmailRequestStatus?: string;
  resetProspectDeleteEmailRequestStatus?: () => void;
  deleteSecondaryPhoneRequestStatus?: string;
  resetProspectDeletePhoneRequestStatus?: () => void;
  resetProspectPrimaryEmailChangeRequestStatus?: () => void;
  resetUpdatePrimaryNumberState?: () => void;
  primaryEmailChangeRequestStatus: RequestStatus;
  primaryPhoneChangeRequestStatus: RequestStatus;
  onActivityChange: (value: boolean) => void;
  fields?: Field[];
  prospectDetailAddField?: boolean;
};

class ProspectProfileForm extends React.Component<IProps> {
  constructor(props: IProps) {
    super(props);

    this.prepareFormConfig = this.prepareFormConfig.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidUpdate(prevProps: Readonly<IProps>): void {
    const {
      deleteSecondaryEmailRequestStatus,
      resetProspectDeleteEmailRequestStatus,
      sendGetSingleProspectDetailsRequest,
      selectedProspectId,
      deleteSecondaryPhoneRequestStatus,
      resetProspectDeletePhoneRequestStatus,
      resetProspectPrimaryEmailChangeRequestStatus,
      resetUpdatePrimaryNumberState,
      primaryEmailChangeRequestStatus,
      primaryPhoneChangeRequestStatus,
      onActivityChange,
      saveContactAttributesRequestStatus,
    } = this.props;
    executeOnRequestStatusWithPrevStatusCheck({
      status: deleteSecondaryEmailRequestStatus,
      prevStatus: prevProps?.deleteSecondaryEmailRequestStatus,
      onSuccess: () => {
        toaster.success('Email address deleted successfully');
        resetProspectDeleteEmailRequestStatus();
        sendGetSingleProspectDetailsRequest(selectedProspectId);
      },
    });
    executeOnRequestStatusWithPrevStatusCheck({
      status: deleteSecondaryPhoneRequestStatus,
      prevStatus: prevProps?.deleteSecondaryPhoneRequestStatus,
      onSuccess: () => {
        toaster.success('Phone number deleted successfully');
        resetProspectDeletePhoneRequestStatus();
        sendGetSingleProspectDetailsRequest(selectedProspectId);
      },
    });
    executeOnRequestStatusWithPrevStatusCheck({
      status: primaryEmailChangeRequestStatus,
      prevStatus: prevProps?.primaryEmailChangeRequestStatus,
      onSuccess: () => {
        toaster.success('Primary Email address updated successfully');
        resetProspectPrimaryEmailChangeRequestStatus();
        sendGetSingleProspectDetailsRequest(selectedProspectId);
        onActivityChange(true);
      },
    });
    executeOnRequestStatusWithPrevStatusCheck({
      status: primaryPhoneChangeRequestStatus,
      prevStatus: prevProps?.primaryPhoneChangeRequestStatus,
      onSuccess: () => {
        toaster.success('Primary Phone number updated successfully');
        resetUpdatePrimaryNumberState();
        sendGetSingleProspectDetailsRequest(selectedProspectId);
        onActivityChange(true);
      },
    });
    executeOnRequestStatusWithPrevStatusCheck({
      status: saveContactAttributesRequestStatus,
      prevStatus: prevProps?.saveContactAttributesRequestStatus,
      onSuccess: () => {
        onActivityChange(true);
      },
    });
  }

  getShouldFormFieldDisabled = (name: string): boolean =>
    name === 'email' || name === 'companydomain' || name === 'creationsource';

  prepareFormConfig(): FormField[] {
    const { selectedProspectData, fields } = this.props;

    if (!fields || fields.length === 0) {
      return [];
    }

    const { attributes } = selectedProspectData || { attributes: [] };

    // Create a map of attribute values by fieldRefId for quick lookup
    const attributeValueMap = new Map<string | number, any>();

    attributes.forEach((attribute: any) => {
      let fieldRefId: string | number | null = null;

      if (attribute?.fieldRef?.id !== undefined) {
        fieldRefId = attribute.fieldRef.id;
      } else if (attribute?.fieldRefId !== undefined) {
        fieldRefId = attribute.fieldRefId;
      }

      if (fieldRefId !== null) {
        attributeValueMap.set(fieldRefId, attribute.attributeValue);
      }
    });

    // Show ALL fields from the fields prop, not just ones with attributes
    return fields.map((field) => {
      const name = field?.label?.replace(/\s+/g, '').toLowerCase();
      const fieldId = field.id?.toString();

      // Get the value from attributes if it exists
      const attributeValue =
        attributeValueMap.get(field.id) ||
        attributeValueMap.get(fieldId) ||
        null;

      return {
        id: field.id,
        isDefault: field.isDefault || false,
        name,
        disabled: this.getShouldFormFieldDisabled(name),
        displayName: field.label,
        component: field.fieldType,
        value: attributeValue,
        styleClassNames: 'col-md-6',

        validator:
          name === 'email'
            ? InputValidationType.isEmail
            : name === 'phonenumber'
            ? InputValidationType.isMobilePhone
            : InputValidationType.MaxCharLimit,
      };
    });
  }

  handleSubmit(values) {
    const { handleSaveContactAttributesSubmit, fields } = this.props;

    if (!fields || fields.length === 0) {
      return;
    }

    // Create payload for all fields that have values or were modified
    const payload = fields
      .map((field) => {
        const name = field?.label?.replace(/\s+/g, '').toLowerCase();
        let fieldValue = values[name];

        // Include field in payload if it has a value or was explicitly set (including empty string)
        // This ensures we can clear fields by setting them to empty
        if (fieldValue !== undefined && fieldValue !== null) {
          // Format date fields to YYYY-MM-DD format
          if (field.fieldType === FieldType.Date) {
            fieldValue = formatDateForApi(fieldValue);
          }
          return {
            attributeValue: fieldValue,
            fieldRef: field.id,
          };
        }

        return null;
      })
      .filter((item) => item !== null);

    handleSaveContactAttributesSubmit(payload);
  }

  render() {
    const {
      getSingleProspectDetailsStatus,
      onClose,
      saveContactAttributesRequestStatus,
      selectedProspectData,
      sendPrimaryEmailChangeInProspectRequest,
      sendPrimaryPhoneChangeInProspectRequest,
      sendDeleteEmailFromProspectRequest,
      sendDeletePhoneFromProspectRequest,
      fields,
      primaryEmailChangeRequestStatus,
      primaryPhoneChangeRequestStatus,
      prospectDetailAddField,
    } = this.props;

    if (
      !selectedProspectData ||
      getSingleProspectDetailsStatus === RequestStatus.Pending ||
      getSingleProspectDetailsStatus === RequestStatus.Failed
    ) {
      return (
        <div className="mt-5 d-flex justify-content-center">
          <Spinner animation={SpinnerAnimation.Border} />
        </div>
      );
    }
    return (
      <Form
        fields={this.prepareFormConfig()}
        formMode={FormMode.Edit}
        formBodyClasses="form-body prospect-modal-user-field"
        formContainerClasses="mr-3"
        onSubmit={this.handleSubmit}
        buttonClassName="mr-5"
        onCancel={onClose}
        submitStatus={getIsRequestPending(saveContactAttributesRequestStatus)}
        selectedProspectId={selectedProspectData?.id}
        primaryEmailChangeInProspectRequest={
          sendPrimaryEmailChangeInProspectRequest
        }
        updatePrimaryNumberRequest={sendPrimaryPhoneChangeInProspectRequest}
        deleteEmailFromProspectRequest={sendDeleteEmailFromProspectRequest}
        deletePhoneFromProspectRequest={sendDeletePhoneFromProspectRequest}
        isAiIconShow={true}
        fieldsData={fields}
        saveContactAttributesRequestStatus={saveContactAttributesRequestStatus}
        primaryEmailChangeRequestStatus={primaryEmailChangeRequestStatus}
        primaryPhoneChangeRequestStatus={primaryPhoneChangeRequestStatus}
        prospectDetailAddField={prospectDetailAddField}
      />
    );
  }
}

export default ProspectProfileForm;
